import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted',
  brand: 'bg-brand-200 text-brand-800',
  success: 'bg-success-soft text-success-text',
  warning: 'bg-warning-soft text-warning-text',
  danger: 'bg-danger-soft text-danger-text',
};

export interface BadgeProps extends ComponentProps<'span'> {
  tone?: BadgeTone;
  /** A 6px dot in the badge's own colour, before the label. */
  dot?: boolean;
  children?: ReactNode;
}

/**
 * A small pill of status. Modelled on the product's cost badges: a tinted
 * ground with the matching deep colour on it, never a saturated fill — a badge
 * is a label, not a button, and reading as one is the point.
 */
export function Badge({ tone = 'neutral', dot, className, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
        'text-2xs font-bold whitespace-nowrap',
        tones[tone],
        className,
      )}
      {...props}
    >
      {dot ? <span className="size-1.5 shrink-0 rounded-full bg-current" /> : null}
      {children}
    </span>
  );
}
